import React, { Fragment, useCallback, useEffect, useState } from "react";
import CommonModal from "../modals/commonModel";
import { GoPlus } from "react-icons/go";
import moment from "moment/moment";
import CalendarCellLoading from "./components/calendarCellLoading";
import { getCalenderEventList } from "../../../services/workspace/holiday.service";
import toast from "react-hot-toast";
import DefaultButton from "../buttons/defaultButton";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import AddReminderForm from "../forms/reminder/addReminderForm";
import { dateFormat } from "../../../utils/helper";
import { formatTime } from "../../../utils/common";

// Utility to generate calendar grid for given month string
const getCalendarGrid = (currentMonthStr) => {
  const startOfMonth = moment(currentMonthStr).startOf("month");
  const endOfMonth = moment(currentMonthStr).endOf("month");
  const startWeekday = startOfMonth.isoWeekday();
  const endWeekday = endOfMonth.isoWeekday();
  const daysBefore = startWeekday - 1;
  const daysAfter = 7 - endWeekday;

  const calendarStart = startOfMonth.clone().subtract(daysBefore, "days");
  const calendarEnd = endOfMonth.clone().add(daysAfter, "days");

  const grid = [];
  const currentDate = calendarStart.clone();

  while (currentDate.isSameOrBefore(calendarEnd, "day")) {
    grid.push({
      date: currentDate.toDate(),
      isCurrentMonth: currentDate.month() === startOfMonth.month(),
    });
    currentDate.add(1, "day");
  }

  return grid;
};

// Weekdays header component
const WeekDaysHeader = () => {
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return (
    <div className="grid grid-cols-7 bg-bghoverlight border-l border-t border-r border-gray-200">
      {days.map((day, index) => (
        <div
          key={index}
          className="text-center text-sm text-theme font-medium border-r border-gray-200 py-4 last:border-r-0"
        >
          {day}
        </div>
      ))}
    </div>
  );
};

// Calendar cells component
const CalendarCells = ({
  currentMonth = "",
  data = [],
  setIsOpen = () => { },
  setIsViewMoreOpen = () => { },
  setSelectedCell = () => { },
}) => {
  const calendarGrid = getCalendarGrid(currentMonth);
  const today = new Date();

  return (
    <div className="grid grid-cols-7 border border-gray-200">
      {calendarGrid.map((cell, idx) => {
        const dateObj = cell.date;
        // Get date string in YYYY-MM-DD format without timezone conversion
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        
        const isToday = today.toDateString() === dateObj.toDateString();
        const isPast = dateObj < today && !isToday;
        const isDisabled = !cell.isCurrentMonth || isPast;

        // Match date by comparing date strings (YYYY-MM-DD format)
        // API returns dates as "YYYY-MM-DD" strings, so compare directly
        const dayData = data.find((d) => {
          if (!d.date) return false;
          // If d.date is already a string in YYYY-MM-DD format, use it directly
          // Otherwise, extract the date part without timezone conversion
          let dDateStr;
          if (typeof d.date === 'string') {
            dDateStr = d.date.split('T')[0]; // Get YYYY-MM-DD part
          } else {
            const dDate = new Date(d.date);
            const dYear = dDate.getFullYear();
            const dMonth = String(dDate.getMonth() + 1).padStart(2, '0');
            const dDay = String(dDate.getDate()).padStart(2, '0');
            dDateStr = `${dYear}-${dMonth}-${dDay}`;
          }
          return dDateStr === dateStr;
        });

        const reminders = Array.isArray(dayData?.reminders) ? dayData.reminders : [];
        const holidays = Array.isArray(dayData?.holidays) ? dayData.holidays : [];
        const events = Array.isArray(dayData?.events) ? dayData.events : [];

        const allItems = [
          ...reminders.map((item) => ({ ...item, type: "reminder" })),
          ...events.map((item) => ({ ...item, type: "event" })),
          ...holidays.map((item) => ({ ...item, type: "holiday" })),
        ];

        const visibleItems = allItems.slice(0, 3);
        const showViewMore = allItems.length > 3;

        const getBgColor = (type) => {
          if (type === "reminder") return "bg-indigo-500 text-white";
          if (type === "event") return "bg-lime-500 text-white";
          if (type === "holiday") return "bg-rose-500 text-white";
          return "";
        };

        return (
          <div
            key={dateStr + idx}
            onClick={(e) => {
              e.stopPropagation();
              setIsViewMoreOpen(true);
              setSelectedCell(dayData);
            }}
            className={`border-r border-b border-gray-200 py-1 h-40 relative overflow-hidden group flex flex-col justify-between gap-1
              ${!cell.isCurrentMonth
                ? "bg-gray-50 text-gray-400 cursor-not-allowed"
                : isPast
                  ? "bg-white text-gray-400 cursor-pointer"
                  : "bg-white hover:bg-light cursor-pointer"
              }
              ${(idx + 1) % 7 === 0 ? 'border-r-0' : ''}
              ${idx >= calendarGrid.length - 7 ? 'border-b-0' : ''}`}
          >
            <div className="text-xs mb-1 flex gap-1 justify-between items-center pl-1 pt-1">
              <span
                className={`font-medium ${isToday
                  ? "bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] text-white rounded-full w-6 h-6 flex items-center justify-center"
                  : ""
                  }`}
              >
                {dateObj.getDate()}
              </span>
            </div>

            {/* Events/Reminders/Holidays Preview */}
            <div className="text-sm overflow-hidden flex flex-col gap-[1px]">
              {visibleItems.map((item, index) => (
                <div
                  key={`${item.type}-${index}`}
                  className={`p-1 text-sm flex justify-between
                     ${getBgColor(item.type)}`}
                >
                  <div className="font-medium text-center px-1 truncate capitalize">
                    {item.name}
                  </div>
                  {item?.tag && item.type === "event" && (
                    <div className="text-[8px] bg-gray-700 text-white rounded-full max-w-12 h-5 flexCenter truncate px-2 capitalize">
                      <span className="truncate">{item.tag}</span>
                    </div>
                  )}
                  {item?.tag && item.type === "reminder" && (
                    <div className="flexCenter text-[8px] px-2 h-4 rounded-md text-nowrap capitalize">
                      {formatTime(item?.time)}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* View More Button */}
            {showViewMore && (
              <div>
                <DefaultButton
                  name="View More"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsViewMoreOpen(true);
                    setSelectedCell(dayData);
                  }}
                  classnames="!text-[10px] !text-center w-full !min-h-4 !bg-transparent !text-theme"
                />
              </div>
            )}

            {/* Add Button */}
            {!isDisabled && (
              <div className="group-hover:block hidden absolute top-1 right-1">
                <DefaultButton
                  icon={<GoPlus />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedCell(dayData);
                    setIsOpen(true);
                  }}
                  variant="iconButton"
                  classnames="!min-w-4 !min-h-4 !w-5 !h-5 !bg-bghoverlight !hover:bg-primary-200 !rounded-full"
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Main calendar component
const DefaultCalendar = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isOpen, setIsOpen] = useState(false);
  const [isViewMoreOpen, setIsViewMoreOpen] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);

  const handlePrevMonth = () => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(currentMonth.getMonth() - 1);

    if (newDate.getFullYear() === new Date().getFullYear()) {
      setCurrentMonth(newDate);
    }
  };

  const handleNextMonth = () => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(currentMonth.getMonth() + 1);

    if (newDate.getFullYear() === new Date().getFullYear()) {
      setCurrentMonth(newDate);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const response = await getCalenderEventList({ date: currentMonth });
      // Ensure response is an array and each item has reminders, holidays, and events arrays
      const normalizedData = Array.isArray(response) 
        ? response.map((item) => {
            const normalizedItem = {
              ...item,
              reminders: Array.isArray(item.reminders) ? item.reminders : [],
              holidays: Array.isArray(item.holidays) ? item.holidays : [],
              events: Array.isArray(item.events) ? item.events : [],
            };
            return normalizedItem;
          })
        : [];
      setTableData(normalizedData);
    } catch (error) {
      console.error('Failed to fetch calendar data:', error);
      toast.error('Failed to fetch data');
      setTableData([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentMonth]);

  useEffect(() => {
    setIsLoading(true);
    const timeout = setTimeout(() => {
      fetchData();
    }, 500); // debounce delay of 5 milliseconds

    return () => clearTimeout(timeout); // clear timeout on cleanup
  }, [fetchData]);

  const updateTablesWithReminders = (newReminder) => {
    if (!newReminder || !selectedCell?.date) {
      fetchData();
      return;
    }

    const updatedData = tableData.map((item) => {
      if (item.date === selectedCell.date) {
        return {
          ...item,
          reminders: [newReminder, ...item.reminders],
        };
      }
      return item;
    });

    setTableData(updatedData);
  };

  return (
    <div className="bg-white rounded-lg overflow-hidden">
      <div className="flex justify-end gap-4 items-center px-4 py-2 text-theme text-sm">
        <span className="flex items-center gap-1">
          <span className="w-[8px] h-[8px] rounded-full bg-indigo-500 inline-block"></span>
          Reminders
        </span>
        <span className="flex items-center gap-1">
          <span className="w-[8px] h-[8px] rounded-full bg-lime-600 inline-block"></span>
          Events
        </span>
        <span className="flex items-center gap-1">
          <span className="w-[8px] h-[8px] rounded-full bg-rose-500 inline-block"></span>
          Holidays
        </span>
      </div>

      <div className="flex justify-between items-center px-4 py-2 bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] text-white">
        <button onClick={handlePrevMonth} className="text-sm font-medium">
          <h2 className="text-sm font-medium !bg-transparent flex items-center">
            <IoIosArrowBack className="text-md"/> Prev
          </h2>
        </button>
        <div className="text-lg font-bold">{moment(currentMonth).format('MMMM YYYY')}</div>
        <button onClick={handleNextMonth} className="text-sm font-medium">
          <h2 className="text-sm font-medium !bg-transparent flex items-center">
            Next <IoIosArrowForward className="text-md"/>
          </h2>
        </button>
      </div>

      <WeekDaysHeader />

      {isLoading ? (
        <CalendarCellLoading />
      ) : (
        <CalendarCells
          currentMonth={currentMonth}
          data={tableData}
          onDayClick={() => setIsOpen(true)}
          setIsOpen={setIsOpen}
          setIsViewMoreOpen={setIsViewMoreOpen}
          setSelectedCell={setSelectedCell}
        />
      )}

      {/* Add Reminder Modal */}
      {isOpen && (
        <CommonModal
          show={isOpen}
          modalSize="lg"
          overflow
          headerTitle={`Add Reminder on ${moment(selectedCell?.date).format("DD MMMM YYYY (dddd)")}`}
          onClose={() => setIsOpen(false)}
        >
          <AddReminderForm
            isModal={true}
            onClose={() => setIsOpen(false)}
            data={selectedCell}
            updateData={updateTablesWithReminders}
          />
        </CommonModal>
      )}

      {/* View More Modal */}
      {isViewMoreOpen && (
        <CommonModal
          show={isViewMoreOpen}
          overflow
          modalSize="2xl"
          headerTitle={`${moment(selectedCell?.date).format("DD MMMM YYYY (dddd)")}`}
          onClose={() => {
            setIsViewMoreOpen(false);
            setSelectedCell(null);
          }}
        >
          <div className="flex flex-col gap-4 text-sm max-h-96 overflow-y-auto">
            {selectedCell?.reminders.length === 0 && selectedCell?.holidays.length === 0 && selectedCell?.events.length === 0 ? (
              <div className="text-center text-gray-600 italic min-h-20 flex flex-col items-center justify-center">
                <p className="text-lg font-semibold">Oops!</p>
                <p>Nothing found for this day.</p>
              </div>
            ) : (
              <Fragment>
                {selectedCell?.reminders.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-col gap-2">
                      {selectedCell?.reminders.map((reminder, idx) => (
                        <div key={idx} className="flex gap-4">
                          <div className="w-4 h-4 rounded-md bg-indigo-500 mt-2" />
                          <div>
                            <div className="font-normal text-2xl capitalize">{reminder.name}</div>
                            <div className="flex items-center gap-2">
                              {reminder?.time && (
                                <div className="text-sm text-gray-600">
                                  {formatTime(reminder?.time)}
                                </div>
                              )}
                              {reminder?.tag && (
                                <div>
                                  |
                                </div>
                              )}
                              {reminder?.tag && (
                                <div className="text-sm text-gray-600">
                                  Tag: {reminder.tag}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedCell?.events.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-col gap-2">
                      {selectedCell?.events.map((event, idx) => (
                        <div key={idx} className="flex gap-4">
                          <div className="w-4 h-4 rounded-md bg-lime-600 mt-2" />
                          <div>
                            <div className="font-normal text-2xl capitalize">{event.name}</div>

                            <div className="flex items-center gap-2">
                              {event?.tag && (
                                <div className="text-sm text-gray-600">
                                  Tag: {event.tag}
                                </div>
                              )}
                              {event?.startTime && (
                                <div>
                                  |
                                </div>
                              )}
                              {event?.startTime && event?.endTime && (
                                <div className="text-sm text-gray-600">
                                  {event.startTime} - {event.endTime}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedCell?.holidays.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-col gap-2">
                      {selectedCell?.holidays.map((holiday, idx) => (
                        <div key={idx} className="flex gap-4">
                          <div className="w-4 h-4 rounded-md bg-rose-600 mt-2" />
                          <div>
                            <div className="font-normal text-2xl capitalize">{holiday.name}</div>
                            <div className="flex items-center gap-2">
                              {holiday?.tag && (
                                <div className="text-sm text-gray-600">
                                  Tag: {holiday.tag}
                                </div>
                              )}
                              {holiday?.startTime && (
                                <div>
                                  |
                                </div>
                              )}
                              {holiday?.startTime && holiday?.endTime && (
                                <div className="text-sm text-gray-600">
                                  {holiday.startTime} - {holiday.endTime}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Fragment>
            )}
          </div>

        </CommonModal>
      )}
    </div>
  );
};

export default DefaultCalendar;
